// @flow

import _ from "lodash";
import * as React from "react";
import { Button } from "react-bootstrap";

import Block from "../Block/Block";
import { correctnessString } from "../../utils/correctness";

import type { Results } from "../../data/results";
import type { Template } from "../../templates/";
import type { CorrectnessString } from "../../utils/correctness";

import "./FeedbackScreen.css";

const SuccessSymbol = () => (
  <span className="FeedbackScreen-Symbol FeedbackScreen-Success">✓</span>
);

const FailureSymbol = () => (
  <span className="FeedbackScreen-Symbol FeedbackScreen-Failure">✗</span>
);

const NeutralSymbol = () => (
  <span className="FeedbackScreen-Symbol FeedbackScreen-Neutral">~</span>
);

const CorrectnessSymbol = ({
  correctness
}: {
  correctness: CorrectnessString
}) => {
  if (correctness === "right") {
    return <SuccessSymbol />;
  }
  if (correctness === "neutral") {
    return <NeutralSymbol />;
  }
  if (correctness === "wrong") {
    return <FailureSymbol />;
  }
  throw new Error(`Unknown correctness: ${correctness}`);
};

const CorrectnessText = ({
  correctness
}: {
  correctness: CorrectnessString
}) => {
  if (correctness === "right") {
    return <span>You got it right.</span>;
  }
  if (correctness === "neutral") {
    return <span>You were indifferent.</span>;
  }
  if (correctness === "wrong") {
    return <span>You got it wrong.</span>;
  }
  throw new Error(`Unknown correctness: ${correctness}`);
};

type FeedbackProps = {
  template: Template,
  results: Results,
  answer: boolean
};

const Feedback = (props: FeedbackProps) => {
  const confidences = _(props.results.toArray())
    .filter({ kind: "response" })
    .map(r => r.confidence)
    .value();
  const correctness = correctnessString(_.last(confidences), props.answer);
  const answerText = props.template.task.answerText[props.answer.toString()];
  return (
    <div className="Feedback">
      <Block className="FeedbackScreen-Answer">
        <p>{props.template.task.prompt}</p>
        <p>
          You ultimately said {_.last(confidences)}%,{" "}
          {correctness === "wrong" ? "but" : "and"} the answer is: {answerText}
        </p>
      </Block>
      <Block className="FeedbackScreen-Feedback">
        <p>
          <CorrectnessText correctness={correctness} />
        </p>
        <p>
          <CorrectnessSymbol correctness={correctness} />
        </p>
      </Block>
    </div>
  );
};

type FeedbackScreenProps = {
  question: string | React.Node,
  template: Template,
  results: Results,
  answer: boolean,
  continue: () => void
};

const FeedbackScreen = (props: FeedbackScreenProps) => {
  const answer = props.answer;
  const hasAnswer = answer !== null;
  return (
    <div className="FeedbackScreen">
      <Block className="FeedbackScreen-Question">{props.question}</Block>
      {hasAnswer && (
        <Feedback
          answer={answer}
          template={props.template}
          results={props.results}
        />
      )}
      <div className="FeedbackScreen-Controls">
        <Button bsSize="large" onClick={props.continue}>
          Next question
        </Button>
      </div>
    </div>
  );
};

export default FeedbackScreen;



// WEBPACK FOOTER //
// ./src/components/Task/FeedbackScreen.js